import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    input,
    InputSignal,
    output,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {LayerStyleService} from '../../../app/modules/layer/services/layer-style.service';
import {ApplicationConfig} from '../../../app/classes/application-config';
import {LegendMetric} from '../../enums/legend-metric.enum';
import {UserPreference} from '../../../app/classes/user-preferences';
import {GenLegendStyle} from 'frontend/generated/serverModels/GenLegendStyle';
import {toSignal} from '@angular/core/rxjs-interop';
import {take} from 'rxjs/operators';

@Component({
    selector: 'eaglei-nom-legend',
    templateUrl: 'nom-legend.component.html',
    styleUrls: ['nom-legend.component.scss'],
    standalone: false,
})
export class NomLegendComponent implements AfterViewInit {
    private overrideText: Signal<ElementRef<HTMLElement>> = viewChild('overrideText');
    private overrideHash: Signal<ElementRef<HTMLElement>> = viewChild('overrideHash');

    /**
     * Controls whether the override toggle functionality is enabled.
     * When true, allows toggling the override style visualization.
     * When false, the override toggle functionality is disabled.
     */
    public allowOverrideToggle = input(true);

    // These are mainly used for the edit preference modal, most of the time they should stay undefined
    public legendMetric: InputSignal<number> = input();
    public legendStyle: InputSignal<GenLegendStyle> = input();

    public toggleOverrideVisibility = output<void>();

    protected preferences: Signal<UserPreference | undefined> = toSignal(ApplicationConfig.currentUserPreferences);

    protected metric = computed(() => {
        if (this.legendMetric()) {
            this.preferences().legendMetric = this.legendMetric();
        }

        return LegendMetric.from(this.preferences().legendMetric) || LegendMetric.CUSTOMERS_OUT;
    });

    protected colors = computed(() => {
        if (this.legendStyle()) {
            this.preferences().legendStyle = this.legendStyle();
        }
        return this.preferences()?.hasLegendStyle() ? this.preferences()?.legendStyle.colors : LayerStyleService.nomRange;
    });

    protected legendElements = computed(() => {
        const value = [];
        let breakPoints = [];
        if (this.metric() === LegendMetric.CUSTOMERS_OUT) {
            breakPoints = LayerStyleService.nomOutageLegend.map((v) => v.display);
        } else if (this.metric() === LegendMetric.PERCENTAGE) {
            breakPoints = LayerStyleService.nomPercentLegend.map((v) => v.display);
        }

        for (let i = 0; i < this.colors().length; i++) {
            value.push({
                display: breakPoints[i],
                color: this.colors()[i],
            });
        }

        return value;
    });

    protected renderOverrideStyle: WritableSignal<boolean> = signal(ApplicationConfig.showOverrideColor.value);

    constructor(public ele: ElementRef) {}

    ngAfterViewInit(): void {
        this.updateOverrideColor();
    }

    private updateOverrideColor(overrideColor?: string): void {
        if (!overrideColor) {
            if (this.preferences()?.hasLegendStyle()) {
                overrideColor = this.preferences().legendStyle.overrideColor;
            } else {
                overrideColor = ApplicationConfig.overrideColor;
            }
        }

        const backgroundImage = `linear-gradient(45deg, ${overrideColor} 25%, transparent 25%, transparent 50%, ${overrideColor} 50%, ${overrideColor} 75%, transparent 75%, #fff)`;

        this.overrideText().nativeElement.style.color = overrideColor;
        this.overrideHash().nativeElement.style.backgroundImage = backgroundImage;
        this.overrideHash().nativeElement.style.borderColor = overrideColor;
    }

    public toggleShowOverrides(): void {
        // This is needed right now to render overrides... We may not need it soon
        this.renderOverrideStyle.update((currentValue) => {
            ApplicationConfig.showOverrideColor.next(!currentValue);
            return !currentValue;
        });

        this.toggleOverrideVisibility.emit();
    }
}
